import { Injectable } from '@angular/core';
import { setting } from '../../../../setting';
import { PaymentApiService } from './payment-api.service';

declare var MercadoPago: any;

/** Datos que devuelve el Card Payment Brick al tokenizar (getFormData) */
export interface MercadoPagoCardData {
  token: string;
  payment_method_id: string;
  issuer_id?: string;
  installments: number;
  transaction_amount?: number;
  payer?: {
    email?: string;
    identification?: { type: string; number: string };
  };
}

/**
 * Integración con el SDK web v2 de Mercado Pago (Card Payment Brick).
 *
 * El formulario de tarjeta lo renderiza Mercado Pago en sus propios iframes: el número,
 * vencimiento y CVV nunca pasan por la app; solo se obtiene un token de un solo uso que el
 * backend usa para cobrar (POST paymentMasters/create con payment_platform_id 2).
 */
@Injectable({
  providedIn: 'root'
})
export class MercadoPagoService {

  private static readonly SDK_URL = 'https://sdk.mercadopago.com/js/v2';
  /** Cuotas máximas que ofrece el formulario */
  private static readonly MAX_INSTALLMENTS = 12;

  private mp: any = null;
  private mpPublicKey: string | null = null;
  private brickController: any = null;
  private sdkLoading: Promise<void> | null = null;

  // Clave pública: la devuelve el backend (get-cards). setting.ts queda solo como respaldo local.
  // private publicKey: string = setting.mercadoPagoPublicKey || '';
  private publicKey =  '';
  private configLoading: Promise<boolean> | null = null;
  private configClientId: number | null = null;
  /** Tarjetas guardadas del cliente en Mercado Pago (get-cards) */
  savedCards: any[] = [];

  constructor(private paymentApiService: PaymentApiService) {}

  /** Hay clave pública (sin ella no se ofrece Mercado Pago) */
  isConfigured(): boolean {
    return !!this.publicKey;
  }

  /**
   * Trae la clave pública (y las tarjetas guardadas) de GET paymentMasters/mercadopago/get-cards/{client}.
   * Se consulta una vez por cliente.
   */
  loadConfig(clientId: number): Promise<boolean> {
    if (!clientId) {
      return Promise.resolve(this.isConfigured());
    }
    if (this.configLoading && this.configClientId === clientId) {
      return this.configLoading;
    }

    this.configClientId = clientId;
    this.configLoading = this.paymentApiService.getMercadoPagoCards(clientId)
      .then((response: any) => {
        const publicKey = response?.public_key
          || response?.publicKey
          || response?.data?.public_key
          || response?.data?.publicKey;

        if (publicKey) {
          this.publicKey = publicKey;
        }
        this.savedCards = Array.isArray(response?.cards) ? response.cards : [];
        return this.isConfigured();
      })
      .catch(() => {
        // Permitir reintentar en la próxima apertura
        this.configLoading = null;
        return this.isConfigured();
      });

    return this.configLoading;
  }

  private loadSdk(): Promise<void> {
    if (typeof MercadoPago !== 'undefined') {
      return Promise.resolve();
    }
    if (this.sdkLoading) {
      return this.sdkLoading;
    }

    this.sdkLoading = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = MercadoPagoService.SDK_URL;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.sdkLoading = null;
        reject(new Error('No se pudo cargar el SDK de Mercado Pago'));
      };
      document.head.appendChild(script);
    });

    return this.sdkLoading;
  }

  async initialize(): Promise<boolean> {
    try {
      if (!this.isConfigured()) {
        throw new Error('Clave pública de Mercado Pago no configurada');
      }

      await this.loadSdk();

      // Se recrea si cambió la clave (otra agencia / entorno)
      if (!this.mp || this.mpPublicKey !== this.publicKey) {
        this.mp = new MercadoPago(this.publicKey, { locale: 'es-AR' });
        this.mpPublicKey = this.publicKey;
      }
      return true;

    } catch (error) {
      console.error('Error inicializando Mercado Pago:', error);
      return false;
    }
  }

  /**
   * Renderiza (o vuelve a renderizar) el formulario de tarjeta en el contenedor.
   * El importe se usa para calcular las cuotas; si cambia hay que volver a renderizar.
   */
  async renderCardForm(containerId: string, amount: number, payerEmail?: string): Promise<boolean> {
    if (!this.mp || !(amount > 0)) {
      return false;
    }

    await this.destroyCardForm();

    try {
      const initialization: any = { amount: Math.round(amount * 100) / 100 };
      if (payerEmail) {
        initialization.payer = { email: payerEmail };
      }

      this.brickController = await this.mp.bricks().create('cardPayment', containerId, {
        initialization,
        customization: {
          visual: {
            // El pago se dispara desde nuestro botón (getCardData), igual que con Square
            hidePaymentButton: true
          },
          paymentMethods: {
            maxInstallments: MercadoPagoService.MAX_INSTALLMENTS
          }
        },
        callbacks: {
          onReady: () => {},
          // Requerido por el Brick; no se usa porque el botón propio está oculto
          onSubmit: () => Promise.resolve(),
          onError: (error: any) => console.error('Error en el formulario de Mercado Pago:', error)
        }
      });

      return true;

    } catch (error) {
      console.error('Error renderizando el formulario de Mercado Pago:', error);
      this.brickController = null;
      return false;
    }
  }

  /**
   * Valida el formulario y tokeniza la tarjeta. Devuelve null si los datos son inválidos
   * (el propio Brick marca los campos con error).
   */
  async getCardData(): Promise<MercadoPagoCardData | null> {
    if (!this.brickController) {
      return null;
    }

    try {
      const data = await this.brickController.getFormData();
      return data?.token ? data : null;
    } catch (error) {
      console.error('Error tokenizando la tarjeta con Mercado Pago:', error);
      return null;
    }
  }

  async destroyCardForm(): Promise<void> {
    if (!this.brickController) {
      return;
    }

    try {
      await this.brickController.unmount();
    } catch (error) {
      // El contenedor pudo haber salido del DOM; no hay nada más que limpiar
    }
    this.brickController = null;
  }
}
